import { useEffect, useState } from "react";

/**
 * ============================================================================
 *  useCompteARebours.ts — L'enchaînement automatique à la fin d'un chapitre
 * ----------------------------------------------------------------------------
 *  Passé le délai, le chapitre suivant démarre seul, SAUF si le visiteur a fait
 *  quoi que ce soit entre-temps : un clic, un toucher, une touche, la molette.
 *  Un geste vaut choix (explorer une scène, ouvrir le menu, revoir…) et l'écran
 *  ne lui reprend pas la main ensuite.
 *
 *  L'annulation survit au démontage : explorer une scène démonte l'écran de fin,
 *  et en revenir ne doit pas relancer un compte à rebours que le visiteur a déjà
 *  refusé. Elle est retenue par chapitre (`cle`) — le chapitre suivant, lui, est
 *  de nouveau automatique.
 *
 *  Le délai est ici, pas dans le document : le schéma du moteur n'a pas de
 *  réglage pour cela, et un nombre n'est pas du contenu éditorial.
 * ==========================================================================*/

/** Délai avant le chapitre suivant, en secondes. */
export const DELAI_ENCHAINEMENT_S = 10;

/** Le chapitre dont le visiteur a refusé l'enchaînement automatique. */
let chapitreAnnule: number | null = null;

export interface CompteARebours {
  /** Secondes restantes, arrondies au-dessus ; `null` : pas de compte à rebours (inactif ou annulé). */
  restant: number | null;
  /** 0 → 1, pour une barre de progression. */
  avancement: number;
}

/**
 * @param actif  Il y a un chapitre suivant à lancer.
 * @param cle    Le chapitre qui se termine (son index).
 * @param surFin Lance le chapitre suivant.
 */
export function useCompteARebours(actif: boolean, cle: number, surFin: () => void): CompteARebours {
  const [ecoule, setEcoule] = useState(0);
  const [annule, setAnnule] = useState(() => chapitreAnnule === cle);

  useEffect(() => {
    setAnnule(chapitreAnnule === cle);
    setEcoule(0);
  }, [cle]);

  const enCours = actif && !annule;

  useEffect(() => {
    if (!enCours) return;
    const annuler = () => {
      chapitreAnnule = cle;
      setAnnule(true);
    };
    // Tout geste du visiteur, où qu'il tombe : le compte à rebours n'est qu'une proposition.
    const gestes = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
    gestes.forEach((g) => window.addEventListener(g, annuler, { passive: true }));

    const debut = performance.now();
    let fini = false;
    const minuteur = window.setInterval(() => {
      const s = (performance.now() - debut) / 1000;
      setEcoule(Math.min(s, DELAI_ENCHAINEMENT_S));
      if (s >= DELAI_ENCHAINEMENT_S && !fini) {
        fini = true;
        window.clearInterval(minuteur);
        surFin();
      }
    }, 100);

    return () => {
      window.clearInterval(minuteur);
      gestes.forEach((g) => window.removeEventListener(g, annuler));
    };
    // `surFin` change à chaque rendu de l'hôte : le relancer remettrait le compte à zéro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCours, cle]);

  if (!enCours) return { restant: null, avancement: 0 };
  return { restant: Math.max(1, Math.ceil(DELAI_ENCHAINEMENT_S - ecoule)), avancement: ecoule / DELAI_ENCHAINEMENT_S };
}
